import { CommonModule } from '@angular/common';
import { Component, computed, inject, Type } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	MDIconComponent,
	MDHeaderComponent,
	MDProgressBarComponent,
	MDContentBoxComponent,
	MDToolTipDirective,
	MDDropDownComponent,
	MDTagComponent,
	MDDialogService
} from '@melodic-ui/components';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonDetailFormStateService } from '../lesson-detail-form-state.service';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { AudioTranscribeInputComponent } from '@lb/components/audio-transcribe';
import { RichTextViewComponent } from '@lb/components/rich-text-view';
import { ViewRubricDialogComponent, toProjectRubricDialogInputs, toRubricTabsFromCriteria } from '../view-rubric-dialog/view-rubric-dialog.component';

@Component({
	selector: 'lb-lesson-detail-project-progress',
	templateUrl: './lesson-detail-project-progress.component.html',
	styleUrl: './lesson-detail-project-progress.component.scss',
	imports: [
		CommonModule,
		FormsModule,
		MDIconComponent,
		MDHeaderComponent,
		MDProgressBarComponent,
		MDContentBoxComponent,
		LoaderComponent,
		MDToolTipDirective,
		MDDropDownComponent,
		AudioTranscribeInputComponent,
		MDTagComponent,
		RichTextViewComponent
	],
	standalone: true
})
export class LessonDetailProjectProgressComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);
	private readonly dialogService = inject(MDDialogService);

	loading = this.state.loading;
	loaded = this.state.loaded;
	lesson = computed(() => this.state.lesson());
	isReadOnly = this.formState.isReadOnly;
	fieldsDisabled = this.formState.fieldsDisabled;

	// Assumption that there is only one project category per lesson, as is currently the case in the data model
	readonly projectCategory = computed(() => this.state.gradingTemplateCategories().find((c) => c.categoryType === GradingTemplateCategoryType.Project));
	readonly stageOptions = computed(() => [...(this.projectCategory()?.milestones ?? [])].sort((a, b) => a.orderNumber - b.orderNumber));
	readonly milestoneOptions = computed(() => this.stageOptions().map((stage) => ({ value: stage.milestoneID, label: stage.milestoneName })));
	readonly completedMilestones = this.formState.completedMilestones;
	readonly selectedProjectMilestoneID = this.formState.selectedProjectMilestoneID;
	readonly savedMilestoneDetails = this.formState.savedMilestoneDetails;
	readonly notes = this.formState.projectNotes;

	readonly selectedMilestoneLabel = computed(() => {
		const id = this.selectedProjectMilestoneID();
		if (id == null) return null;
		return this.milestoneOptions().find((o) => o.value === id)?.label ?? null;
	});

	/** Read-only rubric reference — one tab per project criterion. Grading itself lives in the semester project grade dialog. */
	readonly rubricTabs = computed(() => toRubricTabsFromCriteria(this.projectCategory()?.criteria ?? []));

	isProjectProgressInvalid = () => this.formState.isProjectProgressInvalid();

	progressLabel = computed<string | null>(() => {
		const milestones = this.projectCategory()?.milestones ?? [];
		if (milestones.length === 0) return null;
		// Progress is based on unique milestones reached across the semester.
		const completedCount = new Set(this.savedMilestoneDetails().map((m) => m.projectMilestoneID)).size;
		return `${completedCount}/${milestones.length}`;
	});

	progressPercent = computed(() => {
		if (this.stageOptions() === undefined) {
			return 0;
		}

		const milestones = this.projectCategory()?.milestones ?? [];
		if (milestones.length === 0) return 100;
		// Progress is based on unique milestones reached across the semester.
		const completedCount = new Set(this.savedMilestoneDetails().map((m) => m.projectMilestoneID)).size;
		return Math.round((completedCount / milestones.length) * 100);
	});

	onMilestoneChange(value: unknown): void {
		if (typeof value === 'number' || value === null) {
			this.formState.updateSelectedProjectMilestone(value);
		}
	}

	onNotesChange(value: string): void {
		this.formState.updateProjectNotes(value);
	}

	openRubricDialog(): void {
		this.dialogService.open(ViewRubricDialogComponent as Type<any>, toProjectRubricDialogInputs(this.projectCategory()));
	}
}
